'use client'

import type { ReactNode } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Client-side pathname gate for the layout's Recent Articles section.
 *
 * The section itself arrives as SERVER-RENDERED children from layout.tsx —
 * this component only decides whether to show them. It must NOT import
 * RecentArticles directly: that would pull the entire Contentlayer
 * `allPosts` corpus (7MB raw / ~1.3MB gzip) into the shared client bundle,
 * which is exactly the regression the 2026-07-17 perf audit removed.
 * `services` is the /services footer-image variant, also server-rendered.
 */
const ConditionalRecentArticles = ({
  children,
  services,
}: {
  children: ReactNode
  services?: ReactNode
}) => {
  const pathname = usePathname()

  if (!pathname) {
    return null
  }

  // "Essential Reading" is HOMEPAGE-ONLY as of 2026-08-08, and the homepage
  // renders its own copy in src/app/(app)/page.tsx — so this layout-level gate now
  // renders nothing anywhere.
  //
  // Why: on a commercial page the section sat directly above the closing CTA
  // and offered a 20-minute read at the moment of the ask. It also pushed the
  // CTA down on eight pages. The pillar posts stay reachable from the nav,
  // the footer, and the sitemap, and a sitewide identical link block is
  // boilerplate that search engines discount anyway — so the internal-link
  // value lost is minimal, while the homepage placement (the one with real
  // authority) is kept.
  //
  // The props stay in the signature so re-enabling a page is a one-line
  // pathname check rather than re-plumbing the server-rendered children.
  void children
  void services
  return null
}

export default ConditionalRecentArticles
